import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { SEARCH } from '@/constants/app.constants';

// --- Data ---
const genres = [
  'Rock',
  'Disney',
  'Kids & Family',
  'Party',
  'Country',
  'Pop & Hip-Hop',
  'Instrumental',
  'Jazz',
  'Metal',
  'Folk & Accoustic',
  'Faith & Religion',
  'Comedy',
  'Dance/Electronic',
];

const propsMain = [
  'Arches',
  'Christmas Specific Props',
  'Fans',
  'Halloween Specific Props',
  'Icicles',
  'Mega Tree',
  'Moving Heads',
  'Pixel Poles',
  'Pixel Stakes',
  'Singing Faces',
  'Spinners',
];

const propsChristmas = [
  'Candy Canes',
  'Lamp Posts',
  'Nutcrackers',
  'Ornaments',
  'Reindeer',
  'Snowflakes',
  'Snowmen',
  'Stars',
  'Teddy Bears',
  'Trees',
  'Wreaths',
  'Disney Props',
];

const propsHalloween = [
  'Bats',
  'Ghosts',
  'Halloween Singing Faces',
  'Pumpkins',
  'Spiders',
  'Spooky Tree',
  'Tombstone',
  'Miscellaneous Halloween',
];

// --- Helper ---
const toParam = (str: string) => encodeURIComponent(str.replace(/\s+&\s+/g, ' and ').replace(/\s+/g, ' '));

// --- Component ---
const SequencesMegaMenu: React.FC = () => {
  const navigate = useNavigate();

  // Responsive: open on click for mobile, hover for desktop (handled by NavigationMenu)

  return (
    <NavigationMenu>
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger
            className="font-medium text-foreground bg-transparent rounded-none shadow-none border-none px-2 py-0 h-auto hover:bg-transparent hover:text-primary focus:bg-transparent focus:text-primary transition-colors text-base"
            onClick={e => {
              // Only navigate if the user clicks directly on the text, not the dropdown arrow
              // The arrow is an SVG (ChevronDown), so check if the target is the trigger itself
              if (e.target === e.currentTarget) {
                navigate('/sequences');
              }
            }}
          >
            Sequences
          </NavigationMenuTrigger>
          <NavigationMenuContent className="absolute left-1/2 -translate-x-1/2 w-[98vw] min-w-[600px] max-w-[1600px] p-8 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-x-12 gap-y-8 bg-background rounded-lg shadow-xl border mt-2">
            {/* Christmas */}
            <div>
              <div className="font-semibold text-lg mb-4">Christmas</div>
              <div className="flex flex-col gap-3">
                <NavigationMenuLink asChild>
                  <Link to="/sequences/holiday/Christmas">All Christmas Sequences</Link>
                </NavigationMenuLink>
                <NavigationMenuLink asChild>
                  <Link to={`/sequences/holiday/Christmas?sort=${encodeURIComponent(SEARCH.SORT_OPTIONS.NEWEST_FIRST)}`}>
                    Latest Christmas Sequences
                  </Link>
                </NavigationMenuLink>
              </div>
            </div>
            {/* Halloween */}
            <div>
              <div className="font-semibold text-lg mb-4">Halloween</div>
              <div className="flex flex-col gap-3">
                <NavigationMenuLink asChild>
                  <Link to="/sequences/holiday/Halloween">All Halloween Sequences</Link>
                </NavigationMenuLink>
                <NavigationMenuLink asChild>
                  <Link to={`/sequences/holiday/Halloween?sort=${encodeURIComponent(SEARCH.SORT_OPTIONS.NEWEST_FIRST)}`}>
                    Latest Halloween Sequences
                  </Link>
                </NavigationMenuLink>
              </div>
            </div>
            {/* By Prop */}
            <div>
              <div className="font-semibold text-lg mb-4">By Prop</div>
              <div className="flex flex-col gap-3">
                {propsMain.map((prop) => {
                  if (prop === 'Christmas Specific Props') {
                    return (
                      <div key={prop} className="group relative">
                        <span className="cursor-pointer">{prop}</span>
                        <div className="absolute left-full top-0 z-20 hidden group-hover:block bg-background border rounded-lg shadow-lg min-w-[200px] p-4">
                          {propsChristmas.map((sub) => (
                            <NavigationMenuLink asChild key={sub}>
                              <Link to={`/sequences?props_used=${toParam(sub)}`}>{sub}</Link>
                            </NavigationMenuLink>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  if (prop === 'Halloween Specific Props') {
                    return (
                      <div key={prop} className="group relative">
                        <span className="cursor-pointer">{prop}</span>
                        <div className="absolute left-full top-0 z-20 hidden group-hover:block bg-background border rounded-lg shadow-lg min-w-[200px] p-4">
                          {propsHalloween.map((sub) => (
                            <NavigationMenuLink asChild key={sub}>
                              <Link to={`/sequences?props_used=${toParam(sub)}`}>{sub}</Link>
                            </NavigationMenuLink>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <NavigationMenuLink asChild key={prop}>
                      <Link to={`/sequences?props_used=${toParam(prop)}`}>{prop}</Link>
                    </NavigationMenuLink>
                  );
                })}
              </div>
            </div>
            {/* By Genre */}
            <div>
              <div className="font-semibold text-lg mb-4">By Genre</div>
              <div className="flex flex-col gap-3">
                <NavigationMenuLink asChild>
                  <Link to="/sequences/genres">All Genres</Link>
                </NavigationMenuLink>
                {genres.map((genre) => {
                  const slug = genre.toLowerCase()
                    .replace(/\s*&\s*/g, ' and ')
                    .replace(/\s*\/\s*/g, '-')
                    .replace(/\s+/g, '-')
                    .replace(/[^a-z0-9-]/g, '');
                  return (
                    <NavigationMenuLink asChild key={genre}>
                      <Link to={`/sequences/genre/${slug}`}>{genre}</Link>
                    </NavigationMenuLink>
                  );
                })}
              </div>
            </div>
            {/* Latest */}
            <div>
              <div className="font-semibold text-lg mb-4">Latest</div>
              <div className="flex flex-col gap-3">
                <NavigationMenuLink asChild>
                  <Link to="/sequences?sort=created_at%3Adesc">
                    Newest Sequences
                  </Link>
                </NavigationMenuLink>
              </div>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
};

export default SequencesMegaMenu; 